import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
  Ticket, Plus, Edit, Trash2, X, Save, Copy,
  Percent, DollarSign, Calendar, Users, Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPromoCodesTab({ trips }) {
  const { token } = useAuth();
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_purchase: '',
    max_discount: '',
    usage_limit: '',
    per_user_limit: 1,
    valid_from: '',
    valid_until: '',
    applicable_trips: [],
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      const res = await axios.get(`${API}/promo/codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromoCodes(res.data);
    } catch (err) {
      console.error('Failed to fetch promo codes:', err);
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 10,
      min_purchase: '',
      max_discount: '',
      usage_limit: '',
      per_user_limit: 1,
      valid_from: '',
      valid_until: '',
      applicable_trips: [],
      is_active: true
    });
    setEditingCode(null);
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      description: code.description || '',
      discount_type: code.discount_type,
      discount_value: code.discount_value,
      min_purchase: code.min_purchase || '',
      max_discount: code.max_discount || '',
      usage_limit: code.usage_limit || '',
      per_user_limit: code.per_user_limit || 1,
      valid_from: code.valid_from ? code.valid_from.split('T')[0] : '',
      valid_until: code.valid_until ? code.valid_until.split('T')[0] : '',
      applicable_trips: code.applicable_trips || [],
      is_active: code.is_active
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.code.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    if (!formData.discount_value || formData.discount_value <= 0) {
      toast.error('Please enter a valid discount value');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        code: formData.code.toUpperCase().trim(),
        min_purchase: formData.min_purchase ? parseFloat(formData.min_purchase) : null,
        max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
        per_user_limit: parseInt(formData.per_user_limit) || 1,
        valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : null,
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
        applicable_trips: formData.applicable_trips.length > 0 ? formData.applicable_trips : null
      };

      if (editingCode) {
        await axios.put(`${API}/promo/codes/${editingCode.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Promo code updated');
      } else {
        await axios.post(`${API}/promo/codes`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Promo code created');
      }

      setShowForm(false);
      resetForm();
      fetchPromoCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save promo code');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (codeId) => {
    if (!window.confirm('Delete this promo code?')) return;
    try {
      await axios.delete(`${API}/promo/codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Promo code deleted');
      fetchPromoCodes();
    } catch (err) {
      toast.error('Failed to delete promo code');
    }
  };

  const handleToggle = async (codeId) => {
    try {
      const res = await axios.post(`${API}/promo/codes/${codeId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchPromoCodes();
    } catch (err) {
      toast.error('Failed to toggle promo code');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  const toggleTripSelection = (slug) => {
    setFormData(prev => ({
      ...prev,
      applicable_trips: prev.applicable_trips.includes(slug)
        ? prev.applicable_trips.filter(s => s !== slug)
        : [...prev.applicable_trips, slug]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-promo-codes-tab">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Ticket className="h-5 w-5 text-secondary" />
            Promo Codes
            <Badge variant="outline">{promoCodes.length}</Badge>
          </CardTitle>
          <Button onClick={() => { resetForm(); setShowForm(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Create Code
          </Button>
        </CardHeader>
        <CardContent>
          {promoCodes.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Ticket className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No promo codes yet</p>
              <p className="text-sm mt-1">Create your first promo code to offer discounts</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium text-muted-foreground">Code</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Discount</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Usage</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Validity</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {promoCodes.map((code) => (
                    <tr key={code.id} className="border-t">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <code className="bg-muted px-2 py-1 rounded font-mono font-bold">
                            {code.code}
                          </code>
                          <Button variant="ghost" size="icon" onClick={() => copyCode(code.code)}>
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                        {code.description && (
                          <p className="text-xs text-muted-foreground mt-1">{code.description}</p>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          {code.discount_type === 'percentage' ? (
                            <>
                              <Percent className="h-4 w-4 text-green-600" />
                              <span className="font-semibold text-green-600">{code.discount_value}%</span>
                            </>
                          ) : (
                            <>
                              <DollarSign className="h-4 w-4 text-green-600" />
                              <span className="font-semibold text-green-600">{code.discount_value}</span>
                            </>
                          )}
                        </div>
                        {code.min_purchase && (
                          <p className="text-xs text-muted-foreground">Min: ${code.min_purchase}</p>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span>{code.times_used || 0}</span>
                          {code.usage_limit && <span className="text-muted-foreground">/ {code.usage_limit}</span>}
                        </div>
                      </td>
                      <td className="p-3 text-sm">
                        {code.valid_from || code.valid_until ? (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {code.valid_from && new Date(code.valid_from).toLocaleDateString()}
                            {code.valid_from && code.valid_until && ' - '}
                            {code.valid_until && new Date(code.valid_until).toLocaleDateString()}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">No expiry</span>
                        )}
                      </td>
                      <td className="p-3">
                        <Badge 
                          className={code.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                          onClick={() => handleToggle(code.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          {code.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(code)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(code.id)} className="text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingCode ? 'Edit Promo Code' : 'Create Promo Code'}</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setShowForm(false); resetForm(); }}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Code *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="SUMMER2026"
                    className="font-mono"
                    disabled={!!editingCode}
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Summer sale discount"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label>Discount Type</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>
                <div>
                  <Label>Discount Value *</Label>
                  <Input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) })}
                    placeholder={formData.discount_type === 'percentage' ? '10' : '50'}
                  />
                </div>
                <div>
                  <Label>Max Discount (for %)</Label>
                  <Input
                    type="number"
                    value={formData.max_discount}
                    onChange={(e) => setFormData({ ...formData, max_discount: e.target.value })}
                    placeholder="100"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label>Min Purchase ($)</Label>
                  <Input
                    type="number"
                    value={formData.min_purchase}
                    onChange={(e) => setFormData({ ...formData, min_purchase: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Total Usage Limit</Label>
                  <Input
                    type="number"
                    value={formData.usage_limit}
                    onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                    placeholder="Unlimited"
                  />
                </div>
                <div>
                  <Label>Per User Limit</Label>
                  <Input
                    type="number"
                    value={formData.per_user_limit}
                    onChange={(e) => setFormData({ ...formData, per_user_limit: e.target.value })}
                    placeholder="1"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Valid From</Label>
                  <Input
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Valid Until</Label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label>Applicable Trips (leave empty for all trips)</Label>
                <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg p-2">
                  {trips?.map((trip) => (
                    <div
                      key={trip.slug}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted/50 ${
                        formData.applicable_trips.includes(trip.slug) ? 'bg-secondary/10' : ''
                      }`}
                      onClick={() => toggleTripSelection(trip.slug)}
                    >
                      <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                        formData.applicable_trips.includes(trip.slug) ? 'bg-secondary border-secondary' : 'border-muted-foreground'
                      }`}>
                        {formData.applicable_trips.includes(trip.slug) && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <span className="text-sm">{trip.title}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Active</Label>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : (editingCode ? 'Update Code' : 'Create Code')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
